import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import { CalendarDays, ChevronLeft, ChevronRight, Globe, Plus, Server, Trash2 } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { PersonnelLayout } from '../components/personnel-layout'
import { MobileBottomSlots } from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { confirm } from '../components/ui/confirm-dialog'
import { Dialog } from '../components/ui/dialog'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus } from '../components/ui/mobile-action-bar'
import { SidePanel } from '../components/ui/side-panel'
import { TableShell } from '../components/ui/table-shell'
import { api, type Nonbusinessday, type Nonbusinessgroup } from '../lib/api'
import { notify } from '../lib/notify'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/holidays',
  component: HolidaysPage,
})

function formatDateDE(iso: string): string {
  const d = new Date(iso)
  const dd = String(d.getUTCDate()).padStart(2, '0')
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0')
  const yyyy = d.getUTCFullYear()
  return `${dd}.${mm}.${yyyy}`
}

function HolidaysPage() {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null)
  const [groupDialogOpen, setGroupDialogOpen] = useState(false)
  const [dayPanelOpen, setDayPanelOpen] = useState(false)

  const { data: meData, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const isAdmin = meData?.user?.role === 'admin'

  const { data: groupsData, isLoading: groupsLoading } = useQuery({
    queryKey: ['nonbusinessgroups'],
    queryFn: () => api.listNonbusinessgroups(),
    enabled: isAdmin,
  })

  const groups = groupsData?.groups ?? []

  // Auto-select the first group when groups load and nothing is selected
  const effectiveGroupId = selectedGroupId ?? groups[0]?.id ?? null
  const selectedGroup = groups.find((g) => g.id === effectiveGroupId) ?? null

  const { data: daysData, isLoading: daysLoading } = useQuery({
    queryKey: ['nonbusinessdays', effectiveGroupId, selectedYear],
    queryFn: () =>
      api.listNonbusinessdays({
        // Guarded by `enabled` below — query never fires while effectiveGroupId is null.
        groupId: effectiveGroupId ?? '',
        year: selectedYear,
      }),
    enabled: !!effectiveGroupId,
  })

  const days = daysData?.days ?? []

  // For day counts per group in the sidebar
  const { data: allDaysData } = useQuery({
    queryKey: ['nonbusinessdays-all', selectedYear],
    queryFn: () => api.listNonbusinessdays({ year: selectedYear }),
    enabled: isAdmin,
  })
  const allDays = allDaysData?.days ?? []
  const dayCountByGroup = useMemo(() => {
    const map: Record<string, number> = {}
    for (const d of allDays) {
      map[d.nonbusinessgroupId] = (map[d.nonbusinessgroupId] ?? 0) + 1
    }
    return map
  }, [allDays])

  // Sort days by date
  const sortedDays = useMemo(() => [...days].sort((a, b) => a.date.localeCompare(b.date)), [days])

  if (meLoading) return null
  if (!meData?.user) return <Navigate to="/login" />
  if (!isAdmin) {
    return (
      <PersonnelLayout title="Feiertage">
        <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
          <p className="text-text-secondary">
            Feiertagsverwaltung ist nur für Administratoren sichtbar.
          </p>
        </div>
      </PersonnelLayout>
    )
  }

  return (
    <PersonnelLayout
      title="Feiertage"
      description="Feiertagsgruppen und individuelle Feiertage verwalten."
    >
      {/* Shell-Bottom-Slots (Phone + Tablet-Band): ＋ Feiertag in der Bottom-Bar — derselbe
          Handler wie der Gruppen-Kopf-Button; ohne gewählte Gruppe
          deaktiviert statt versteckt (layout-stabil). Gruppe-hinzufügen +
          Jahr-◀▶ bleiben im Content (Mapping §3). */}
      <MobileBottomSlots>
        <BarAccentPlus
          ariaLabel="Feiertag hinzufügen"
          onClick={() => setDayPanelOpen(true)}
          disabled={!effectiveGroupId}
          title={!effectiveGroupId ? 'Wähle zuerst eine Feiertagsgruppe' : undefined}
        />
      </MobileBottomSlots>

      {/* Header row with controls */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2">
          <Button onClick={() => setGroupDialogOpen(true)} size="sm">
            <Plus className="h-4 w-4" />
            Gruppe hinzufügen
          </Button>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setSelectedYear((y) => y - 1)}
            className="p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-overlay transition-colors touch:opacity-100"
            aria-label="Vorheriges Jahr"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium tabular-nums min-w-[3.5rem] text-center">
            {selectedYear}
          </span>
          <button
            type="button"
            onClick={() => setSelectedYear((y) => y + 1)}
            className="p-2 rounded-md text-text-secondary hover:text-text-primary hover:bg-bg-overlay transition-colors touch:opacity-100"
            aria-label="Nächstes Jahr"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Main layout: sidebar + content */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Group list (sidebar on desktop, top on mobile) */}
        <div className="w-full lg:w-72 xl:w-80 shrink-0">
          {groupsLoading ? (
            <p className="text-sm text-text-secondary">Lade Gruppen …</p>
          ) : groups.length === 0 ? (
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-6 text-center">
              <CalendarDays className="h-8 w-8 mx-auto text-text-tertiary mb-2" />
              <p className="text-sm text-text-secondary">Keine Feiertagsgruppen vorhanden.</p>
            </div>
          ) : (
            <ul className="overflow-hidden rounded-2xl border border-border-subtle bg-bg-elevated/40">
              {groups.map((g, i) => (
                <li key={g.id}>
                  <button
                    type="button"
                    onClick={() => setSelectedGroupId(g.id)}
                    className={cn(
                      'w-full text-left px-4 py-3 flex items-center gap-3 transition-colors touch:opacity-100',
                      effectiveGroupId === g.id
                        ? 'bg-accent/10 border-l-2 border-l-accent'
                        : 'hover:bg-bg-overlay/40 border-l-2 border-l-transparent',
                      i < groups.length - 1 && 'border-b border-border-subtle',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-text-primary truncate">{g.name}</p>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-info/15 border border-info/40 px-1.5 py-0.5 text-2xs text-info">
                          <Globe className="h-2.5 w-2.5" />
                          {g.country}
                          {g.region ? `/${g.region}` : ''}
                        </span>
                        {g.isBuiltin && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-info/15 border border-info/40 px-1.5 py-0.5 text-2xs text-info">
                            <Server className="h-2.5 w-2.5" />
                            System
                          </span>
                        )}
                      </div>
                    </div>
                    <span className="text-xs text-text-tertiary tabular-nums shrink-0">
                      {dayCountByGroup[g.id] ?? 0}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Main content area */}
        <div className="flex-1 min-w-0">
          {!effectiveGroupId ? (
            <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
              <CalendarDays className="h-10 w-10 mx-auto text-text-tertiary mb-3" />
              <p className="text-text-secondary">Wähle eine Feiertagsgruppe aus der Liste.</p>
            </div>
          ) : (
            <>
              {/* Group header with actions */}
              <div className="flex items-center justify-between gap-3 mb-4">
                <div className="min-w-0">
                  <h2 className="text-lg font-medium truncate">{selectedGroup?.name}</h2>
                  <div className="flex items-center gap-2 mt-0.5">
                    {selectedGroup?.isBuiltin && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-info/15 border border-info/40 px-2 py-0.5 text-2xs text-info">
                        <Server className="h-2.5 w-2.5" />
                        System
                      </span>
                    )}
                    <span className="text-xs text-text-tertiary">
                      {sortedDays.length} {sortedDays.length === 1 ? 'Feiertag' : 'Feiertage'} in{' '}
                      {selectedYear}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  {/* <640 übernimmt der ＋ der Bottom-Bar — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
                  <Button size="sm" onClick={() => setDayPanelOpen(true)} className="phone:hidden">
                    <Plus className="h-4 w-4" />
                    <span className="hidden sm:inline">Feiertag hinzufügen</span>
                  </Button>
                  {selectedGroup && !selectedGroup.isBuiltin && (
                    <DeleteGroupButton
                      group={selectedGroup}
                      onDeleted={() => setSelectedGroupId(null)}
                    />
                  )}
                </div>
              </div>

              {/* Holidays table */}
              {daysLoading ? (
                <p className="text-sm text-text-secondary">Lade Feiertage …</p>
              ) : sortedDays.length === 0 ? (
                <div className="rounded-2xl border border-border-subtle bg-bg-elevated/40 p-8 text-center">
                  <CalendarDays className="h-10 w-10 mx-auto text-text-tertiary mb-3" />
                  <p className="text-text-secondary">
                    Keine Feiertage für {selectedYear} in dieser Gruppe.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-4"
                    onClick={() => setDayPanelOpen(true)}
                  >
                    <Plus className="h-4 w-4" />
                    Feiertag hinzufügen
                  </Button>
                </div>
              ) : (
                <TableShell className="rounded-2xl border-border-subtle bg-bg-elevated/40">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border-subtle bg-bg-overlay/30">
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                          Datum
                        </th>
                        <th className="text-left px-4 py-2.5 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                          Name
                        </th>
                        <th className="text-center px-4 py-2.5 text-xs font-medium text-text-tertiary uppercase tracking-wider">
                          Halbtag
                        </th>
                        <th className="text-right px-4 py-2.5 text-xs font-medium text-text-tertiary uppercase tracking-wider w-20">
                          Aktionen
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedDays.map((day, i) => (
                        <DayRow key={day.id} day={day} isLast={i === sortedDays.length - 1} />
                      ))}
                    </tbody>
                  </table>
                </TableShell>
              )}
            </>
          )}
        </div>
      </div>

      {/* Create group dialog */}
      <CreateGroupDialog open={groupDialogOpen} onClose={() => setGroupDialogOpen(false)} />

      {/* Add day side panel */}
      {effectiveGroupId && (
        <AddDayPanel
          open={dayPanelOpen}
          onClose={() => setDayPanelOpen(false)}
          groupId={effectiveGroupId}
          year={selectedYear}
        />
      )}
    </PersonnelLayout>
  )
}

/* ── Day row ──────────────────────────────────────────────────────── */

function DayRow({ day, isLast }: { day: Nonbusinessday; isLast: boolean }) {
  const queryClient = useQueryClient()
  const toggleHalfDay = useMutation({
    mutationFn: () => api.updateNonbusinessday(day.id, { halfDay: !day.halfDay }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays'] })
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays-all'] })
      // Feiertage ändern das Soll → Berichte/Stundenkonto mit invalidieren.
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success(day.halfDay ? 'Ganztag gesetzt' : 'Halbtag gesetzt')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const remove = useMutation({
    mutationFn: () => api.deleteNonbusinessday(day.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays'] })
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays-all'] })
      // Feiertage ändern das Soll → Berichte/Stundenkonto mit invalidieren.
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success('Feiertag gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <tr
      className={cn(
        'group transition-colors hover:bg-bg-overlay/40',
        !isLast && 'border-b border-border-subtle',
      )}
    >
      <td className="px-4 py-2.5 text-text-primary tabular-nums whitespace-nowrap">
        {formatDateDE(day.date)}
      </td>
      <td className="px-4 py-2.5 text-text-primary">
        <span className="truncate">{day.name}</span>
        {day.halfDay && (
          <span className="ml-2 inline-flex items-center rounded-full bg-warning/15 border border-warning/40 px-1.5 py-0.5 text-2xs text-warning font-medium">
            ½
          </span>
        )}
      </td>
      <td className="px-4 py-2.5 text-center">
        <label className="inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={day.halfDay}
            onChange={() => toggleHalfDay.mutate()}
            disabled={toggleHalfDay.isPending}
            className="h-4 w-4 accent-accent cursor-pointer"
          />
        </label>
      </td>
      <td className="px-4 py-2.5 text-right">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={async () => {
            const ok = await confirm({
              title: 'Feiertag löschen',
              description: `„${day.name}“ am ${formatDateDE(day.date)} wird gelöscht.`,
              variant: 'danger',
              confirmLabel: 'Löschen',
            })
            if (ok) remove.mutate()
          }}
          aria-label="Löschen"
          className="text-text-tertiary hover:text-danger opacity-0 group-hover:opacity-100 touch:opacity-100"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </td>
    </tr>
  )
}

/* ── Delete group button ──────────────────────────────────────────── */

function DeleteGroupButton({
  group,
  onDeleted,
}: {
  group: Nonbusinessgroup
  onDeleted: () => void
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.deleteNonbusinessgroup(group.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nonbusinessgroups'] })
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays'] })
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays-all'] })
      // Feiertage ändern das Soll → Berichte/Stundenkonto mit invalidieren.
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success('Gruppe gelöscht')
      onDeleted()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      onClick={async () => {
        const ok = await confirm({
          title: 'Gruppe löschen',
          description: `„${group.name}“ und alle zugehörigen Feiertage werden unwiderruflich gelöscht.`,
          variant: 'danger',
          confirmLabel: 'Löschen',
        })
        if (ok) remove.mutate()
      }}
      aria-label="Gruppe löschen"
      className="text-text-tertiary hover:text-danger"
    >
      <Trash2 className="h-4 w-4" />
    </Button>
  )
}

/* ── Create group dialog ──────────────────────────────────────────── */

function CreateGroupDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient()
  const [name, setName] = useState('')
  const [country, setCountry] = useState('DE')
  const [region, setRegion] = useState('')

  const save = useMutation({
    mutationFn: () =>
      api.createNonbusinessgroup({
        name,
        country: country || 'DE',
        region: region || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nonbusinessgroups'] })
      notify.success('Feiertagsgruppe angelegt')
      setName('')
      setCountry('DE')
      setRegion('')
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      notify.error('Name ist Pflicht')
      return
    }
    save.mutate()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Neue Feiertagsgruppe"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={save.isPending}>
            Anlegen
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4 pb-2">
        <div className="space-y-2">
          <Label htmlFor="group-name">Name</Label>
          <Input
            id="group-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Betriebsfeiertage"
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="group-country">Land</Label>
          <Input
            id="group-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            placeholder="DE"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="group-region">Region (optional)</Label>
          <Input
            id="group-region"
            value={region}
            onChange={(e) => setRegion(e.target.value)}
            placeholder="z.B. BY, NRW"
          />
        </div>
      </form>
    </Dialog>
  )
}

/* ── Add day side panel ───────────────────────────────────────────── */

function AddDayPanel({
  open,
  onClose,
  groupId,
  year,
}: {
  open: boolean
  onClose: () => void
  groupId: string
  year: number
}) {
  const queryClient = useQueryClient()
  const [date, setDate] = useState('')
  const [name, setName] = useState('')
  const [halfDay, setHalfDay] = useState(false)

  const save = useMutation({
    mutationFn: () => api.createNonbusinessday({ groupId, date, name, halfDay }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays'] })
      queryClient.invalidateQueries({ queryKey: ['nonbusinessdays-all'] })
      // Feiertage ändern das Soll → Berichte/Stundenkonto mit invalidieren.
      queryClient.invalidateQueries({ queryKey: ['reports'] })
      queryClient.invalidateQueries({ queryKey: ['time-accounts'] })
      notify.success('Feiertag hinzugefügt')
      setDate('')
      setName('')
      setHalfDay(false)
      onClose()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!date || !name.trim()) {
      notify.error('Datum und Name sind Pflicht')
      return
    }
    save.mutate()
  }

  // Default the date picker to the selected year range
  const minDate = `${year}-01-01`
  const maxDate = `${year}-12-31`

  return (
    <SidePanel
      open={open}
      onClose={onClose}
      title="Feiertag hinzufügen"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} type="button">
            Abbrechen
          </Button>
          <Button onClick={onSubmit} type="button" disabled={save.isPending}>
            Hinzufügen
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="day-date">Datum</Label>
          <Input
            id="day-date"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={minDate}
            max={maxDate}
            autoFocus
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="day-name">Name</Label>
          <Input
            id="day-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="z.B. Heiligabend"
          />
        </div>
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={halfDay}
            onChange={(e) => setHalfDay(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          <div>
            <span className="text-sm font-medium">Halbtag</span>
            <p className="text-xs text-text-tertiary">Nur ein halber Feiertag (z.B. Heiligabend)</p>
          </div>
        </label>
      </form>
    </SidePanel>
  )
}
